import React, { useEffect } from 'react';
import { Button, Container, lighten, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';

import { selectedDps } from '../../atoms/DspState';
import { AxlButton } from '../../components/AxlButton';
import ListHeader from '../../components/ListHeader';
import Pagination from '../../components/Pagination';
import { LOADED_LIST_DSP, LOADING_LIST_DSP, dspSelector } from '../../reducers/DspReducer';
import { listDsp } from '../../repositories/HttpRepository';
import ActionButton from './ActionButton';
import SearchForm from './SearchForm';

const fields = [
  { id: 'id', numeric: false, disablePadding: true, label: 'id' },
  { id: 'company', numeric: false, disablePadding: false, label: 'Company' },
  { id: 'code', numeric: false, disablePadding: false, label: 'Alias' },
  { id: 'regions', numeric: false, disablePadding: false, label: 'Regions' },
  { id: 'email', numeric: false, disablePadding: false, label: 'Email' },
  {
    id: 'phone_number',
    numeric: false,
    disablePadding: false,
    label: 'Phone Number',
  },
  {
    id: 'totalDriver',
    numeric: false,
    disablePadding: false,
    label: 'Total Driver',
    isDisableSort: true,
  },
  { id: 'actions', numeric: false, isDisableSort: true },
];

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
    border: '1px solid #e0e0eb',
  },
  table: {
    minWidth: 750,
    // Header row is all <th>; body rows start with a <th scope="row"> (the id
    // cell) followed by <td>s. nth-of-type counts per element type, so the body
    // td index is offset by one from the visual column. Highlight the first 3
    // header columns (id, Company, Alias) and the first 2 body tds (Company,
    // Alias) — the body id column is covered by the th rule below.
    '& tr th:nth-of-type(-n+3)': {
      backgroundColor: lighten(theme.palette.primary.main, 0.9),
    },
    '& tr td:nth-of-type(-n+2)': {
      backgroundColor: lighten(theme.palette.primary.main, 0.9),
      color: '#7e7d7e',
    },
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
    marginBottom: '10px',
  },
  cellOnClick: {
    '&>a': {
      color: '#4a90e2',
    },
    '&:hover': {
      cursor: 'pointer',
    },
  },
  searchBtn: {
    backgroundColor: '#75c31e',
  },
}));
const ListDsp = () => {
  const [state, updateValues] = useRecoilState(dspSelector);
  const navigate = useNavigate();
  const updateSelectDsp = useSetRecoilState(selectedDps);
  const { items, page, size, desc, order_by, total, isListing, q } = state;

  const { classes } = useStyles();

  const getList = (params) => {
    let queryParams = { page, size, q };
    if (params) queryParams = { ...queryParams, ...params };

    updateValues({ type: LOADING_LIST_DSP });
    listDsp(queryParams).then((resp) => {
      if (resp.ok) {
        updateValues({
          type: LOADED_LIST_DSP,
          data: resp.data,
          params: queryParams,
        });
      }
    });
  };

  useEffect(() => {
    getList();
  }, [q]);
  const handleRequestSort = (event, property) => {
    const isDesc = order_by === property && desc;
    getList({ page: page, order_by: property, desc: !isDesc, q: q });
  };
  const handleChangePage = (event, newPage) => {
    getList({ page: newPage - 1, order_by, desc, q: q });
  };

  const addAction = (e) => {
    e.preventDefault();
    navigate('/dsp/add');
  };
  const showDrivers = (e, courierId, courierObj) => {
    e.preventDefault();
    updateSelectDsp(courierObj);
    navigate(`/dsp/drivers/${courierId}`);
  };
  return (
    <Container maxWidth={false}>
      <div className={classes.buttonWrapper}>
        <AxlButton variant="contained" color="primary" onClick={addAction} className={classes.searchBtn}>
          + NEW
        </AxlButton>
      </div>
      <SearchForm getList={getList} initValue={q} placeholder="Search for DSP by company name, alias…" />
      <Paper className={classes.paper}>
        <TableContainer>
          <Table className={classes.table} aria-labelledby="tableTitle" aria-label="enhanced table">
            <ListHeader fields={fields} desc={desc} orderBy={order_by} onRequestOrder={handleRequestSort} rowCount={items.length} />
            <TableBody>
              {items.map((item, index) => {
                const labelId = `dsp-table-item-${index}`;
                return (
                  <TableRow hover tabIndex={-1} key={item.id}>
                    <TableCell component="th" id={labelId} scope="row">
                      {item.id}
                    </TableCell>
                    <TableCell>{item.company}</TableCell>
                    <TableCell>{item.code}</TableCell>
                    <TableCell>{item.regions?.join(',')}</TableCell>
                    <TableCell>{item.email}</TableCell>
                    <TableCell>{item.phone_number}</TableCell>
                    <TableCell className={classes.cellOnClick}>
                      <Button variant="outlined" color="primary" disableElevation onClick={(event) => showDrivers(event, item.id, item)}>
                        {item.totalDriver || 0}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <ActionButton id={item.id} displayCourierAction={() => updateSelectDsp(item)} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {isListing && <LinearProgress />}
        </TableContainer>
        <Pagination total={total} size={size} onChange={handleChangePage} />
      </Paper>
    </Container>
  );
};
export default React.memo(ListDsp);
